import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import CollegeHeader from './CollegeHeader';
import CollegeFooter from './CollegeFooter';
import CertificateCongratsModal from '../components/Certificate/CertificateCongratsModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const T = {
  border:  '1px solid var(--border-subtle, #E2E8F0)',
  radius:  { sm: '8px', md: '12px', lg: '14px', xl: '18px', '2xl': '24px', full: '999px' },
  shadow:  { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)', lg: '0 12px 40px rgba(0,0,0,0.14)', xl: '0 24px 64px rgba(0,0,0,0.18)' },
  brand:   'var(--brand-primary, var(--primary-blue, #4F46E5))',
  text:    { heading: '#0F172A', body: '#374151', muted: '#6B7280', disabled: '#9CA3AF' },
  bg:      { page: '#F8FAFC', card: '#FFFFFF', soft: '#F1F5F9', hover: 'rgba(0,0,0,0.02)' },
  status:  { ACTIVE: { bg: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' }, REVOKED: { bg: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' } },
  grade:   { A: { bg: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' }, B: { bg: '#EFF6FF', color: '#4F46E5', border: '1px solid #BFDBFE' }, C: { bg: '#FEF9C3', color: '#CA8A04', border: '1px solid #FDE047' }, D: { bg: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' } },
};

const inputBase = {
  width: '100%', padding: '10px 13px', fontSize: '0.875rem', fontWeight: 500,
  color: T.text.body, background: '#FFFFFF', border: T.border,
  borderRadius: T.radius.md, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const inputErr  = { borderColor: '#F87171', boxShadow: '0 0 0 3px rgba(248,113,113,0.12)' };
const inputFocus = { borderColor: '#4F46E5', boxShadow: '0 0 0 3px rgba(79,70,229,0.1)' };
const labelBase = { display: 'block', fontSize: '0.68rem', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 };

/* ─── Shared sub-components ─────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const s = T.status[status] || { bg: T.bg.soft, color: T.text.muted, border: T.border };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: T.radius.full, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', background: s.bg, color: s.color, border: s.border }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />{status}
    </span>
  );
};

const GradeBadge = ({ grade }) => {
  const key = grade?.charAt(0) || 'D';
  const g = T.grade[key] || T.grade['D'];
  return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: T.radius.full, fontSize: '0.75rem', fontWeight: 700, background: g.bg, color: g.color, border: g.border }}>{grade || '-'}</span>;
};

const Spinner = ({ size = 20, color = '#FFFFFF' }) => (
  <>
    <style>{`@keyframes _spin { to { transform: rotate(360deg); } }`}</style>
    <div style={{ width: size, height: size, borderRadius: '50%', border: `2.5px solid ${color}40`, borderTopColor: color, animation: '_spin 0.7s linear infinite', flexShrink: 0 }} />
  </>
);

const FieldError = ({ msg }) => msg ? (
  <p style={{ margin: '5px 0 0', fontSize: '0.7rem', color: '#DC2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    {msg}
  </p>
) : null;

const SectionHeader = ({ icon, title, color = '#4F46E5', bg = 'rgba(79,70,229,0.07)' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid rgba(79,70,229,0.1)' }}>
    <div style={{ width: 32, height: 32, borderRadius: 9, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
    <span style={{ fontSize: '0.72rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.09em' }}>{title}</span>
  </div>
);

/* ─── Logo Upload Zone ───────────────────────────────────────────────────── */
const LogoUploadZone = ({ value, onChange, error }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const processFile = useCallback((file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2 MB'); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) { alert('Please upload PNG, JPG, JPEG, or SVG'); return; }
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  }, [onChange]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  }, [processFile]);

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !value && inputRef.current?.click()}
        style={{
          border: `2px dashed ${error ? '#F87171' : dragOver ? '#4F46E5' : '#C7D2FE'}`,
          borderRadius: T.radius.xl, padding: value ? '12px' : '28px 20px',
          textAlign: 'center', cursor: value ? 'default' : 'pointer',
          background: dragOver ? 'rgba(79,70,229,0.04)' : value ? '#F8FAFC' : '#FAFBFF',
          transition: 'all 0.2s',
        }}
      >
        {value ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img src={value} alt="College Logo" style={{ height: 64, maxWidth: 140, objectFit: 'contain', borderRadius: 8, border: T.border, padding: 4, background: '#fff' }} />
            <div style={{ flex: 1, textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: '#16A34A' }}>✓ Logo uploaded</p>
              <p style={{ margin: '2px 0 8px', fontSize: '0.72rem', color: T.text.muted }}>PNG / JPG / SVG · max 2 MB</p>
              <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4F46E5', background: 'rgba(79,70,229,0.08)', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
                Replace Logo
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(79,70,229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
            <p style={{ margin: '0 0 4px', fontSize: '0.875rem', fontWeight: 700, color: T.text.body }}>Drag &amp; drop your logo here</p>
            <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: T.text.muted }}>PNG, JPG, JPEG, SVG · Max 2 MB</p>
            <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4F46E5', background: 'rgba(79,70,229,0.08)', border: '1.5px solid rgba(79,70,229,0.2)', borderRadius: 8, padding: '6px 16px', cursor: 'pointer' }}>
              Browse File
            </button>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" style={{ display: 'none' }} onChange={(e) => processFile(e.target.files?.[0])} />
      </div>
      <FieldError msg={error} />
    </div>
  );
};

/* ─── Live Preview Panel ─────────────────────────────────────────────────── */
const LivePreview = ({ student, formData, collegeLogo, collegeName }) => {
  const isEmpty = !student && !formData.programName && !formData.authorityName;
  if (isEmpty) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 280, gap: 12, padding: 24, textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(79,70,229,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
      </div>
      <div>
        <p style={{ margin: 0, fontWeight: 700, color: T.text.body, fontSize: '0.875rem' }}>Live Preview</p>
        <p style={{ margin: '4px 0 0', color: T.text.muted, fontSize: '0.78rem' }}>Fill in the form to see a summary</p>
      </div>
    </div>
  );

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ padding: 20 }}>
      <p style={{ margin: '0 0 12px', fontSize: '0.65rem', fontWeight: 800, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.09em' }}>Certificate Summary</p>
      {/* Mini certificate mockup */}
      <div style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #1E3A5F 100%)', borderRadius: 14, padding: '18px 16px', color: '#fff', marginBottom: 14, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          {collegeLogo
            ? <img src={collegeLogo} alt="Logo" style={{ height: 28, maxWidth: 70, objectFit: 'contain', background: '#fff', borderRadius: 4, padding: 2 }} />
            : <div style={{ width: 28, height: 28, borderRadius: 4, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#a5b4fc' }}>LOGO</div>
          }
          <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#a5b4fc', letterSpacing: '0.06em' }}>THESKILLGENIE</span>
        </div>
        <p style={{ margin: '0 0 4px', fontSize: '0.6rem', color: '#a5b4fc', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Certificate of Completion</p>
        <p style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{student?.name || 'Student Name'}</p>
        <p style={{ margin: '0 0 10px', fontSize: '0.72rem', color: '#c7d2fe', lineHeight: 1.4 }}>{formData.programName || 'Program Name'}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {formData.overallGrade && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: 'rgba(255,255,255,0.12)', color: '#c7d2fe', padding: '2px 8px', borderRadius: 999 }}>Grade: {formData.overallGrade}</span>}
          {formData.programType && <span style={{ fontSize: '0.6rem', fontWeight: 700, background: 'rgba(255,255,255,0.12)', color: '#c7d2fe', padding: '2px 8px', borderRadius: 999 }}>{formData.programType}</span>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[
          { label: 'Completion', value: fmt(formData.completionDate) },
          { label: 'Issue Date', value: fmt(formData.issueDate) },
          { label: 'Score', value: formData.performanceScore ? `${formData.performanceScore}%` : '—' },
          { label: 'Tasks', value: formData.completedTasks && formData.totalTasks ? `${formData.completedTasks}/${formData.totalTasks}` : '—' },
          { label: 'Commits', value: formData.githubCommits || '—' },
          { label: 'PRs', value: formData.githubPRs || '—' },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: '#F8FAFC', border: T.border, borderRadius: 8, padding: '8px 10px' }}>
            <p style={{ margin: 0, fontSize: '0.58rem', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', fontWeight: 700, color: T.text.body }}>{value}</p>
          </div>
        ))}
      </div>
      {formData.authorityName && (
        <div style={{ marginTop: 10, background: 'rgba(79,70,229,0.05)', border: '1px solid rgba(79,70,229,0.15)', borderRadius: 8, padding: '8px 12px' }}>
          <p style={{ margin: 0, fontSize: '0.6rem', fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Institution Authority</p>
          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', fontWeight: 700, color: T.text.body }}>{formData.authorityName}</p>
          {collegeName && <p style={{ margin: '1px 0 0', fontSize: '0.7rem', color: T.text.muted }}>{collegeName}</p>}
        </div>
      )}
    </div>
  );
};

/* ─── Success Screen ─────────────────────────────────────────────────────── */
const SuccessScreen = ({ certificate, onClose, onGenerateAnother }) => {
  const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const verifyUrl = certificate?.secureToken
    ? `${window.location.origin}/verify/${encodeURIComponent(certificate.secureToken)}`
    : certificate?.verificationUrl || null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: T.radius['2xl'], width: '100%', maxWidth: 520, boxShadow: T.shadow.xl, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', padding: '32px 32px 24px', textAlign: 'center', position: 'relative' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>Certificate Generated!</h2>
          <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>The digital certificate has been issued and is ready to use.</p>
        </div>
        {/* Details */}
        <div style={{ padding: '24px 28px' }}>
          <div style={{ background: '#F8FAFC', borderRadius: 12, border: T.border, padding: '16px 18px', marginBottom: 20 }}>
            {[
              { label: 'Certificate ID', value: certificate?.certificateNumber, mono: true },
              { label: 'Student', value: certificate?.studentName },
              { label: 'Program', value: certificate?.programName },
              { label: 'Generated', value: fmt(certificate?.issueDate) },
            ].map(({ label, value, mono }) => value ? (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: T.text.body, fontFamily: mono ? 'monospace' : 'inherit', background: mono ? 'rgba(79,70,229,0.07)' : 'transparent', padding: mono ? '2px 8px' : 0, borderRadius: mono ? 6 : 0 }}>{value}</span>
              </div>
            ) : null)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            {certificate?.certificatePdfUrl && (
              <a href={certificate.certificatePdfUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <button style={{ width: '100%', padding: '10px 14px', borderRadius: T.radius.md, background: '#4F46E5', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download PDF
                </button>
              </a>
            )}
            {verifyUrl && (
              <a href={verifyUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <button style={{ width: '100%', padding: '10px 14px', borderRadius: T.radius.md, background: '#059669', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Verify Certificate
                </button>
              </a>
            )}
          </div>
          {verifyUrl && (
            <button
              onClick={() => { navigator.clipboard.writeText(verifyUrl); }}
              style={{ width: '100%', padding: '9px 14px', borderRadius: T.radius.md, background: 'rgba(79,70,229,0.07)', color: '#4F46E5', border: '1.5px solid rgba(79,70,229,0.2)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginBottom: 10 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              Copy Verification Link
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button onClick={onGenerateAnother} style={{ padding: '9px 14px', borderRadius: T.radius.md, background: T.bg.soft, color: T.text.body, border: T.border, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
              Generate Another
            </button>
            <button onClick={onClose} style={{ padding: '9px 14px', borderRadius: T.radius.md, background: T.text.heading, color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
const CollegeCertificateManagement = () => {
  const [certificates, setCertificates] = useState([]);
  const [students, setStudents]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [generating, setGenerating]     = useState(false);
  const [error, setError]               = useState(null);
  const [success, setSuccess]           = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [modalOpen, setModalOpen]       = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [congratsModalOpen, setCongratsModalOpen] = useState(false);
  const [successScreen, setSuccessScreen]         = useState(null);
  const [generatedCertificate, setGeneratedCertificate] = useState(null);
  const [collegeLogo, setCollegeLogo]   = useState('');
  const [collegeName, setCollegeName]   = useState('');
  const [fieldErrors, setFieldErrors]   = useState({});

  const user       = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId  = user?.id || user?.collegeId || user?.college_id;
  const authHeader = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const defaultForm = {
    programName: '', programType: 'BOOTCAMP',
    completionDate: new Date().toISOString().split('T')[0],
    issueDate: new Date().toISOString().split('T')[0],
    overallGrade: 'A+', performanceScore: 95,
    totalTasks: 110, completedTasks: 110,
    githubCommits: 0, githubPRs: 0,
    authorityName: '',
  };
  const [formData, setFormData] = useState(defaultForm);

  /* ── Data fetching ───────────────────────────────────────────────────── */
  useEffect(() => { fetchCertificates(); fetchStudents(); fetchCollegeProfile(); }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      if (!collegeId) { setError('College ID not found. Please log in again.'); return; }
      const res = await axios.get(`${API_BASE_URL}/api/certificates/college/${collegeId}`, { headers: authHeader });
      if (res.data.success) setCertificates(res.data.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load certificates');
    } finally { setLoading(false); }
  };

  const fetchStudents = async () => {
    try {
      if (!collegeId) return;
      const res = await axios.get(`${API_BASE_URL}/api/college/${collegeId}/students`, { headers: authHeader });
      if (res.data.success) setStudents(res.data.data);
    } catch (err) { console.error(err); }
  };

  const fetchCollegeProfile = async () => {
    try {
      if (!collegeId) return;
      const res = await axios.get(`${API_BASE_URL}/colleges/${collegeId}`, { headers: authHeader });
      const data = res.data?.data || res.data;
      if (data?.college_name) setCollegeName(data.college_name);
      if (data?.logo_url) setCollegeLogo(data.logo_url);
    } catch (_) {}
  };

  /* ── Auto-fill program when student selected ─────────────────────────── */
  useEffect(() => {
    if (!selectedStudent) return;
    const s = students.find(x => String(x.id) === String(selectedStudent));
    setFormData(prev => ({ ...prev, programName: s?.program_name || prev.programName }));
  }, [selectedStudent, students]);

  /* ── Modal helpers ───────────────────────────────────────────────────── */
  const openModal = () => {
    setModalOpen(true); setSelectedStudent('');
    setFormData(defaultForm); setFieldErrors({}); setError(null);
  };
  const closeModal = () => { setModalOpen(false); setError(null); setFieldErrors({}); };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  /* ── Validation ──────────────────────────────────────────────────────── */
  const validate = () => {
    const e = {};
    if (!selectedStudent)              e.student       = 'Please select a student.';
    if (!formData.programName.trim())  e.programName   = 'Program name is required.';
    if (!formData.programType)         e.programType   = 'Program type is required.';
    if (!formData.completionDate)      e.completionDate = 'Completion date is required.';
    if (!formData.issueDate)           e.issueDate     = 'Issue date is required.';
    if (!formData.overallGrade.trim()) e.overallGrade  = 'Overall grade is required.';
    if (formData.performanceScore === '' || formData.performanceScore === null || formData.performanceScore === undefined)
                                       e.performanceScore = 'Performance score is required.';
    if (!formData.authorityName.trim()) e.authorityName = 'Institution Authority Name is required.';
    if (formData.authorityName.length > 100) e.authorityName = 'Max 100 characters.';
    if (!collegeLogo)                  e.collegeLogo   = 'College logo is required.';
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const isFormValid =
    !!selectedStudent &&
    !!formData.programName.trim() &&
    !!formData.completionDate &&
    !!formData.issueDate &&
    !!formData.overallGrade.trim() &&
    (formData.performanceScore !== '' && formData.performanceScore !== null && formData.performanceScore !== undefined) &&
    !!formData.authorityName.trim() &&
    !!collegeLogo;

  /* ── Generate certificate ────────────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!validate()) return;
    setGenerating(true); setError(null);
    try {
      const student = students.find(s => String(s.id) === String(selectedStudent));
      const res = await axios.post(
        `${API_BASE_URL}/api/certificates/create-dual`,
        {
          studentId: selectedStudent,
          programName: formData.programName,
          programType: formData.programType,
          completionDate: formData.completionDate,
          issueDate: formData.issueDate,
          overallGrade: formData.overallGrade,
          performanceScore: Number(formData.performanceScore),
          totalTasks: Number(formData.totalTasks),
          completedTasks: Number(formData.completedTasks),
          githubCommits: Number(formData.githubCommits),
          githubPRs: Number(formData.githubPRs),
          authorityName: formData.authorityName,
          collegeLogo: collegeLogo,
          hodApproved: false,
          hodName: null,
          hodApprovedAt: null,
        },
        { headers: authHeader }
      );

      if (res.data.success) {
        const d = res.data.data;
        const certData = {
          secureToken: d.secureToken,
          certificateNumber: d.certificateNumber,
          verificationUrl: d.verificationUrl,
          certificatePdfUrl: d.certificatePdfUrl,
          studentId: selectedStudent,
          studentName: student?.name,
          programName: formData.programName,
          overallGrade: formData.overallGrade,
          completedTasks: formData.completedTasks,
          totalTasks: formData.totalTasks,
          issueDate: formData.issueDate || new Date().toISOString(),
        };
        setGeneratedCertificate(certData);
        closeModal();
        fetchCertificates();
        setSuccessScreen(certData);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to generate certificate. Please try again.');
    } finally { setGenerating(false); }
  };

  /* ── Certificate actions ─────────────────────────────────────────────── */
  const handleView = (cert) => {
    const url = cert.certificate_pdf_url || (cert.secure_token ? `${window.location.origin}/verify/${encodeURIComponent(cert.secure_token)}` : null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else { setError('Verification link not available.'); setTimeout(() => setError(null), 4000); }
  };

  const handleDownload = async (cert) => {
    if (cert.certificate_pdf_url) { window.open(cert.certificate_pdf_url, '_blank'); return; }
    const token = cert.secure_token || cert.secureToken;
    if (!token) { setError('Download not available.'); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/certificates/download/${encodeURIComponent(token)}`, { headers: authHeader });
      if (res.data?.downloadUrl) window.open(res.data.downloadUrl, '_blank');
    } catch { setError('Failed to download. Please try again.'); setTimeout(() => setError(null), 4000); }
  };

  /* ── Filtered list ───────────────────────────────────────────────────── */
  const filtered = certificates.filter(c => {
    const q = searchQuery.toLowerCase();
    const matchQ = !q || c.student_name?.toLowerCase().includes(q) || c.certificate_number?.toLowerCase().includes(q) || c.program_name?.toLowerCase().includes(q);
    return matchQ && (filterStatus === 'ALL' || c.status === filterStatus);
  });

  const selStudent = students.find(s => String(s.id) === String(selectedStudent));

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="college-main-layout" style={{ background: T.bg.page }}>
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 1300, margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(79,70,229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                </div>
                <div>
                  <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: T.text.heading, letterSpacing: '-0.02em', margin: 0 }}>Certificate Management</h1>
                  <p style={{ color: T.text.muted, fontSize: '0.875rem', margin: '4px 0 0' }}>Issue, view and manage all institutional digital certificates</p>
                </div>
              </div>
              <button onClick={openModal} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: T.radius.md, background: '#4F46E5', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 700, boxShadow: '0 2px 8px rgba(79,70,229,0.28)', transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                Generate Certificate
              </button>
            </div>

            {/* Alerts */}
            {success && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', marginBottom: 20, background: '#DCFCE7', border: '1px solid #BBF7D0', borderRadius: T.radius.md, color: '#15803D' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{success}</span>
                </div>
                <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
              </div>
            )}
            {error && !modalOpen && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', marginBottom: 20, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: T.radius.md, color: '#DC2626' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{error}</span>
                </div>
                <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
              </div>
            )}

            {/* Stats strip */}
            {!loading && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
                {[
                  { label: 'Total Certificates', value: certificates.length, color: '#4F46E5', iconBg: 'rgba(79,70,229,0.08)', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg> },
                  { label: 'Active',             value: certificates.filter(c => c.status === 'ACTIVE').length,   color: '#16A34A', iconBg: 'rgba(22,163,74,0.08)',  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> },
                  { label: 'Revoked',            value: certificates.filter(c => c.status === 'REVOKED').length,  color: '#DC2626', iconBg: 'rgba(220,38,38,0.08)',  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg> },
                  { label: 'Total Views',        value: certificates.reduce((s, c) => s + (c.views_count || 0), 0), color: '#7C3AED', iconBg: 'rgba(124,58,237,0.08)', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
                ].map((stat, i) => (
                  <div key={i} style={{ background: '#fff', borderRadius: T.radius.lg, padding: 20, border: T.border, boxShadow: T.shadow.xs, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: stat.color, borderRadius: `${T.radius.lg} ${T.radius.lg} 0 0` }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
                      <div>
                        <p style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>{stat.label}</p>
                        <p style={{ fontSize: '1.875rem', fontWeight: 800, color: stat.color, margin: 0, lineHeight: 1 }}>{stat.value}</p>
                      </div>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: stat.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{stat.icon}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filter bar */}
            <div style={{ background: T.bg.card, border: T.border, borderRadius: T.radius.lg, boxShadow: T.shadow.xs, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #4F46E5, #7C3AED)', borderRadius: `${T.radius.lg} ${T.radius.lg} 0 0` }} />
              <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text.muted} strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input type="text" placeholder="Search by student, certificate ID, or program…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  style={{ ...inputBase, paddingLeft: 38 }}
                  onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
              </div>
              <div style={{ flex: '0 0 160px' }}>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputBase}
                  onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })}>
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: T.radius.full, background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.14)', fontSize: '0.8rem', fontWeight: 700, color: '#4F46E5', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {filtered.length} {filtered.length === 1 ? 'Certificate' : 'Certificates'}
              </span>
            </div>

            {/* Table */}
            <div style={{ background: T.bg.card, border: T.border, borderRadius: T.radius.lg, boxShadow: T.shadow.xs, overflow: 'hidden', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #059669, #4F46E5, #7C3AED)', zIndex: 1 }} />
              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: 16, marginTop: 4 }}>
                  <Spinner size={32} color="#4F46E5" />
                  <p style={{ color: T.text.muted, fontSize: '0.875rem', fontWeight: 600, margin: 0 }}>Loading certificates…</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border-subtle,#E2E8F0)' }}>
                        {['Certificate ID', 'Student Name', 'Program', 'Grade', 'Issue Date', 'Views', 'Status', 'Actions'].map((h, i) => (
                          <th key={h} style={{ padding: '11px 16px', textAlign: i === 7 ? 'center' : 'left', fontSize: '0.65rem', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={8} style={{ padding: '60px 24px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 52, height: 52, borderRadius: 14, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                            </div>
                            <p style={{ color: T.text.body, fontWeight: 700, fontSize: '0.9375rem', margin: 0 }}>{searchQuery || filterStatus !== 'ALL' ? 'No matching certificates' : 'No certificates yet'}</p>
                            <p style={{ color: T.text.muted, fontSize: '0.8125rem', margin: 0 }}>{searchQuery || filterStatus !== 'ALL' ? 'Try adjusting your search or filter criteria.' : 'Click "Generate Certificate" to issue the first one.'}</p>
                          </div>
                        </td></tr>
                      ) : filtered.map((cert, idx) => (
                        <tr key={cert.id} style={{ borderBottom: idx < filtered.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '13px 16px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, color: '#4F46E5', background: 'rgba(79,70,229,0.06)', padding: '3px 8px', borderRadius: T.radius.sm }}>
                              {cert.certificate_number || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(79,70,229,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 800, color: '#4F46E5', flexShrink: 0 }}>
                                {cert.student_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: T.text.body }}>{cert.student_name || '-'}</span>
                            </div>
                          </td>
                          <td style={{ padding: '13px 16px', maxWidth: 220 }}>
                            <span style={{ fontSize: '0.8125rem', color: T.text.body, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 220 }} title={cert.program_name}>{cert.program_name || '-'}</span>
                          </td>
                          <td style={{ padding: '13px 16px' }}><GradeBadge grade={cert.overall_grade} /></td>
                          <td style={{ padding: '13px 16px' }}>
                            <span style={{ fontSize: '0.8125rem', color: T.text.muted, fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                            </span>
                          </td>
                          <td style={{ padding: '13px 16px' }}><span style={{ fontSize: '0.8125rem', color: T.text.muted, fontWeight: 600 }}>{cert.views_count || 0}</span></td>
                          <td style={{ padding: '13px 16px' }}><StatusBadge status={cert.status} /></td>
                          <td style={{ padding: '13px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                              {[
                                { fn: () => handleView(cert), title: 'View', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
                                { fn: () => handleDownload(cert), title: 'Download', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> },
                              ].map(({ fn, title, icon }) => (
                                <button key={title} onClick={fn} title={title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: T.radius.sm, background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.14)', cursor: 'pointer', transition: 'background 0.12s' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,70,229,0.12)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(79,70,229,0.06)'}>
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>

      {/* ─── Generate Certificate Modal ─────────────────────────────────────── */}
      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 16px', overflowY: 'auto' }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{ background: '#fff', borderRadius: T.radius['2xl'], width: '100%', maxWidth: 1020, boxShadow: T.shadow.xl, border: T.border, overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>

            {/* Accent strip */}
            <div style={{ height: 5, background: 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 50%, #059669 100%)' }} />

            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 18px', borderBottom: T.border }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(79,70,229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '1.0625rem', fontWeight: 800, color: T.text.heading, margin: 0, letterSpacing: '-0.01em' }}>Generate New Certificate</h2>
                  <p style={{ fontSize: '0.75rem', color: T.text.muted, margin: '2px 0 0' }}>Complete all required fields to issue a verified institutional certificate</p>
                </div>
              </div>
              <button onClick={closeModal} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text.muted} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Two-column layout: Form | Preview */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', minHeight: 0, flex: 1, overflow: 'hidden' }}>

              {/* ── LEFT: scrollable form ── */}
              <div style={{ overflowY: 'auto', padding: '24px 28px', maxHeight: '76vh' }}>

                {/* Error inside modal */}
                {error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: T.radius.md, marginBottom: 22, color: '#DC2626' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{error}</span>
                  </div>
                )}

                {/* ── Section 1: Student Information ── */}
                <div style={{ marginBottom: 28 }}>
                  <SectionHeader
                    title="Student Information"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
                  />
                  <div style={{ display: 'grid', gap: 16 }}>
                    {/* Select Student */}
                    <div>
                      <label style={{ ...labelBase }}>Select Student <span style={{ color: '#DC2626' }}>*</span></label>
                      <select value={selectedStudent} onChange={e => { setSelectedStudent(e.target.value); setFieldErrors(p => ({ ...p, student: '' })); }}
                        style={{ ...inputBase, ...(fieldErrors.student ? inputErr : {}) }}
                        onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })}>
                        <option value="">— Select a student —</option>
                        {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll_number})</option>)}
                      </select>
                      <FieldError msg={fieldErrors.student} />
                    </div>
                    {/* Auto-filled read-only fields */}
                    {selStudent && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                          <label style={labelBase}>Roll Number</label>
                          <input readOnly value={selStudent.roll_number || '—'} style={{ ...inputBase, background: '#F8FAFC', color: T.text.muted }} />
                        </div>
                        <div>
                          <label style={labelBase}>College Name</label>
                          <input readOnly value={collegeName || '—'} style={{ ...inputBase, background: '#F8FAFC', color: T.text.muted }} />
                        </div>
                      </div>
                    )}
                    {/* Program Name & Type */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={labelBase}>Program Name <span style={{ color: '#DC2626' }}>*</span></label>
                        <input type="text" name="programName" value={formData.programName} onChange={handleFormChange}
                          placeholder="Full Stack Development Bootcamp"
                          style={{ ...inputBase, ...(fieldErrors.programName ? inputErr : {}) }}
                          onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                        <FieldError msg={fieldErrors.programName} />
                      </div>
                      <div>
                        <label style={labelBase}>Program Type <span style={{ color: '#DC2626' }}>*</span></label>
                        <select name="programType" value={formData.programType} onChange={handleFormChange}
                          style={{ ...inputBase, ...(fieldErrors.programType ? inputErr : {}) }}
                          onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })}>
                          <option value="BOOTCAMP">Bootcamp</option>
                          <option value="COURSE">Course</option>
                          <option value="WORKSHOP">Workshop</option>
                        </select>
                        <FieldError msg={fieldErrors.programType} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Section 2: Academic Details ── */}
                <div style={{ marginBottom: 28 }}>
                  <SectionHeader
                    title="Academic Details"
                    color="#059669" bg="rgba(5,150,105,0.08)"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={labelBase}>Completion Date <span style={{ color: '#DC2626' }}>*</span></label>
                      <input type="date" name="completionDate" value={formData.completionDate} onChange={handleFormChange}
                        style={{ ...inputBase, ...(fieldErrors.completionDate ? inputErr : {}) }}
                        onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                      <FieldError msg={fieldErrors.completionDate} />
                    </div>
                    <div>
                      <label style={labelBase}>Issue Date <span style={{ color: '#DC2626' }}>*</span></label>
                      <input type="date" name="issueDate" value={formData.issueDate} onChange={handleFormChange}
                        style={{ ...inputBase, ...(fieldErrors.issueDate ? inputErr : {}) }}
                        onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                      <FieldError msg={fieldErrors.issueDate} />
                    </div>
                    <div>
                      <label style={labelBase}>Overall Grade <span style={{ color: '#DC2626' }}>*</span></label>
                      <input type="text" name="overallGrade" value={formData.overallGrade} onChange={handleFormChange}
                        placeholder="A+"
                        style={{ ...inputBase, ...(fieldErrors.overallGrade ? inputErr : {}) }}
                        onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                      <FieldError msg={fieldErrors.overallGrade} />
                    </div>
                  </div>
                </div>

                {/* ── Section 3: Performance Metrics ── */}
                <div style={{ marginBottom: 28 }}>
                  <SectionHeader
                    title="Performance Metrics"
                    color="#7C3AED" bg="rgba(124,58,237,0.08)"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                    {[
                      { label: 'Performance Score (%)', name: 'performanceScore', required: true, min: 0, max: 100 },
                      { label: 'Total Tasks',           name: 'totalTasks' },
                      { label: 'Completed Tasks',       name: 'completedTasks' },
                      { label: 'GitHub Commits',        name: 'githubCommits' },
                      { label: 'Pull Requests',         name: 'githubPRs' },
                    ].map(({ label, name, required, min, max }) => (
                      <div key={name} style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ ...labelBase, minHeight: 32, display: 'flex', alignItems: 'flex-end', marginBottom: 6 }}>{label}{required && <span style={{ color: '#DC2626' }}>&nbsp;*</span>}</label>
                        <input type="number" name={name} value={formData[name]} onChange={handleFormChange}
                          {...(min !== undefined ? { min } : {})} {...(max !== undefined ? { max } : {})}
                          style={{ ...inputBase, ...(fieldErrors[name] ? inputErr : {}) }}
                          onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                        <FieldError msg={fieldErrors[name]} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Section 4: Authority & Branding ── */}
                <div style={{ marginBottom: 8 }}>
                  <SectionHeader
                    title="Authority & Branding"
                    color="#B45309" bg="rgba(180,83,9,0.08)"
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
                  />
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <label style={labelBase}>Institution Authority Name <span style={{ color: '#DC2626' }}>*</span></label>
                      <input type="text" name="authorityName" value={formData.authorityName} onChange={handleFormChange}
                        placeholder="Dr. Rajesh Kumar" maxLength={100}
                        style={{ ...inputBase, ...(fieldErrors.authorityName ? inputErr : {}) }}
                        onFocus={e => Object.assign(e.target.style, inputFocus)} onBlur={e => Object.assign(e.target.style, { borderColor: 'var(--border-subtle,#E2E8F0)', boxShadow: 'none' })} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <FieldError msg={fieldErrors.authorityName} />
                        <span style={{ fontSize: '0.65rem', color: T.text.muted, marginLeft: 'auto' }}>{formData.authorityName.length}/100</span>
                      </div>
                    </div>
                    <div>
                      <label style={labelBase}>College Logo <span style={{ color: '#DC2626' }}>*</span></label>
                      <LogoUploadZone value={collegeLogo} onChange={(val) => { setCollegeLogo(val); setFieldErrors(p => ({ ...p, collegeLogo: '' })); }} error={fieldErrors.collegeLogo} />
                    </div>
                  </div>
                </div>

              </div>

              {/* ── RIGHT: Live Preview ── */}
              <div style={{ borderLeft: T.border, background: '#FAFBFF', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <div style={{ padding: '16px 18px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.09em', paddingBottom: 12 }}>Live Preview</p>
                </div>
                <LivePreview student={selStudent} formData={formData} collegeLogo={collegeLogo} collegeName={collegeName} />
              </div>
            </div>

            {/* ── Sticky Modal Footer ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 28px', borderTop: T.border, background: '#FAFAFA', flexShrink: 0 }}>
              <p style={{ margin: 0, fontSize: '0.75rem', color: T.text.muted }}>
                {isFormValid
                  ? <span style={{ color: '#16A34A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>All required fields complete</span>
                  : <span>Fields marked <span style={{ color: '#DC2626', fontWeight: 700 }}>*</span> are required</span>}
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={closeModal} style={{ padding: '10px 22px', borderRadius: T.radius.md, border: T.border, background: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: T.text.body, transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  Cancel
                </button>
                <button onClick={handleGenerate} disabled={generating || !isFormValid}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: T.radius.md, background: generating || !isFormValid ? '#94A3B8' : '#4F46E5', color: '#fff', border: 'none', cursor: generating || !isFormValid ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 700, boxShadow: generating || !isFormValid ? 'none' : '0 2px 8px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (!generating && isFormValid) e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                  {generating ? <><Spinner size={16} />&nbsp;Generating…</> : <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>Generate Certificate</>}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ─── Success Screen ──────────────────────────────────────────────────── */}
      {successScreen && (
        <SuccessScreen
          certificate={successScreen}
          onClose={() => { setSuccessScreen(null); setCongratsModalOpen(true); }}
          onGenerateAnother={() => { setSuccessScreen(null); openModal(); }}
        />
      )}

      {/* ─── Congrats Modal ──────────────────────────────────────────────────── */}
      <CertificateCongratsModal
        open={congratsModalOpen}
        onClose={() => setCongratsModalOpen(false)}
        certificate={generatedCertificate}
      />
      <CollegeFooter />
      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
      `}</style>
    </div>
  );
};

export default CollegeCertificateManagement;
